import { useEffect, useMemo, useState } from 'react';
import { categoryTabs, stickersAll } from '@/mocks/memes';

interface MemeModal {
  id: string;
  phrase: string;
  caption: string;
  img: string;
  gif?: boolean;
}

export default function MemeLibrary() {
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState<MemeModal | null>(null);

  const list = useMemo(() => {
    const kw = query.trim();
    return stickersAll.filter((s) => {
      const okCat = active === 'all' || s.tag === active;
      const okKw = !kw || s.phrase.includes(kw) || s.caption.includes(kw) || s.tag.includes(kw);
      return okCat && okKw;
    });
  }, [active, query]);

  const countOf = (key: string) =>
    key === 'all' ? stickersAll.length : stickersAll.filter((s) => s.tag === key).length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSel(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <section id="library" className="bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
            <i className="ri-emotion-laugh-line" /> 海量表情随挑随用
          </span>
          <h2 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">吃瓜表情大全</h2>
          <p className="mt-3 text-base text-foreground-700">
            围观现场的万能配图：震惊、无语、偷笑、蹲后续、阴阳怪气……按场景挑一张最对味的表情，聊天更有梗，吃瓜更带劲。
          </p>
        </div>

        {/* 搜索 */}
        <div className="mt-8 flex max-w-xl items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-1">
          <i className="ri-search-line text-base text-foreground-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索表情关键词，如：震惊 / 蹲后续…"
            className="w-full bg-transparent py-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="清空搜索" className="text-foreground-400 hover:text-foreground-600">
              <i className="ri-close-circle-fill text-lg" />
            </button>
          )}
        </div>

        {/* 分类 */}
        <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1">
          {categoryTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === t.key
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {t.label}
              <span className={`ml-1.5 text-xs ${active === t.key ? 'text-background-50/80' : 'text-foreground-400'}`}>
                {countOf(t.key)}
              </span>
            </button>
          ))}
        </div>

        {/* 表情卡片墙 */}
        {list.length > 0 ? (
          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((m) => (
              <button
                key={m.id}
                onClick={() => setSel(m)}
                className="group overflow-hidden rounded-xl border border-background-200 bg-background-50 text-left transition-transform hover:-translate-y-1"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={m.img}
                    alt={`吃瓜群众表情${m.phrase}`}
                    title={`吃瓜表情 ${m.phrase}`}
                    className="aspect-square w-full object-cover"
                  />
                  {m.gif && (
                    <span className="absolute right-2 top-2 rounded-full bg-background-950/60 px-2 py-0.5 text-[11px] text-background-50">
                      动图
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center bg-primary-500/90 py-2 text-sm text-background-50 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <i className="ri-zoom-in-line mr-1" /> 点击预览
                  </div>
                </div>
                <div className="p-3">
                  <p className="truncate text-sm font-medium text-foreground-900">{m.phrase}</p>
                  <p className="mt-1 truncate text-xs text-foreground-500">{m.caption}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="text-3xl text-foreground-300 ri-emotion-sad-line" />
            <p className="text-sm text-foreground-600">没有找到匹配的表情，换个关键词试试吧</p>
          </div>
        )}
      </div>

      {/* 预览弹层 */}
      {sel && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/60 p-4"
          onClick={() => setSel(null)}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-2xl bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img src={sel.img} alt={`吃瓜表情${sel.phrase}`} className="aspect-square w-full object-cover" />
              <button
                onClick={() => setSel(null)}
                aria-label="关闭预览"
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background-950/60 text-background-50 hover:bg-background-950/80"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="truncate text-base font-medium text-foreground-900">{sel.phrase}</p>
                <p className="mt-0.5 truncate text-xs text-foreground-500">{sel.caption}</p>
              </div>
              <button
                onClick={() => setSel(null)}
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
              >
                收藏这张
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}